<script setup>
import { computed, ref, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Dropdown select primitive with filterable options, validation, and multiple selection.
 *
 * Filtering is caller-driven: with `useInput`, attach a `filter` listener to narrow `options`.
 * Accepting text that isn't in `options` requires `newValueMode` or a `new-value` listener.
 *
 * @status internal
 * @group forms-input
 * @event filter Fired when the filter text changes, and once as the menu opens; receives (value, update, abort).
 * @event filter-abort Fired when an in-flight filter is aborted.
 * @event input-value Fired when the typed text changes, including the text `fillInput` writes
 *   after a selection; payload is the raw string.
 * @event new-value Fired when submitted text isn't in `options`; receives (value, doneFn).
 * @event add Fired when a value is added; payload is { index, value }. Only with `multiple`.
 * @event remove Fired when a value is removed; payload is { index, value }. Only with `multiple`.
 * @event clear Fired when the user clears the field via the `clearable` icon.
 * @event popup-show Fired when the options dropdown opens.
 * @event popup-hide Fired when the options dropdown closes.
 * @event focus Fired when the field gains focus.
 * @event blur Fired when the field loses focus.
 * @see https://quasar.dev/vue-components/select Quasar q-select API
 */
defineOptions({ name: 'HrSelect', inheritAttrs: false });

const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /** Currently selected value(s). Use with v-model. */
  modelValue: {
    type: null,
    default: null,
  },
  /**
   * Array of option objects or primitive values to populate the dropdown.
   */
  options: {
    type: Array,
    default: () => [],
  },
  /** Floating label text shown above the field. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /**
   * Property name on each option object to use as the display label.
   */
  optionLabel: {
    type: String,
    default: 'label',
  },
  /**
   * Property name on each option object to use as the emitted value.
   */
  optionValue: {
    type: String,
    default: 'value',
  },
  /**
   * When true, emits the option's value property instead of the full option object.
   * Must be paired with mapOptions to display the label correctly.
   */
  emitValue: {
    type: Boolean,
    default: false,
  },
  /**
   * When true, maps the stored primitive value back to its option object for display.
   * Required when emitValue is true.
   */
  mapOptions: {
    type: Boolean,
    default: false,
  },
  /**
   * Allows selecting multiple values. modelValue becomes an array.
   */
  multiple: {
    type: Boolean,
    default: false,
  },
  /**
   * Shows a text input inside the field for filtering options.
   */
  useInput: {
    type: Boolean,
    default: false,
  },
  /**
   * Hides the selected value's chip/label so the typed filter text shows in its place.
   * Rendering only — combine with `useInput` and `fillInput` for the single-line
   * type-to-filter look. Accepting values that aren't in `options` additionally
   * requires `newValueMode` (or a `new-value` listener).
   */
  hideSelected: {
    type: Boolean,
    default: false,
  },
  /**
   * Fills the text input with the selected option's label instead of clearing it after selection.
   * Rendering only — see `hideSelected`; it does not by itself allow arbitrary typed values.
   */
  fillInput: {
    type: Boolean,
    default: false,
  },
  /**
   * Debounce in milliseconds for the filter input. Only relevant when useInput is true.
   */
  inputDebounce: {
    type: [String, Number],
    default: 0,
  },
  /**
   * Maximum number of selections allowed. Only relevant when multiple is true.
   */
  maxValues: {
    type: Number,
    default: null,
  },
  /**
   * Controls how new values typed into the input are handled.
   * @values add, add-unique, toggle
   */
  newValueMode: {
    type: [String, null],
    default: null,
    validator: (v) => v === null || ['add', 'add-unique', 'toggle'].includes(v),
  },
  /**
   * Overrides the text shown for the current selection in the closed field.
   */
  displayValue: {
    type: String,
    default: null,
  },
  /** Shows a clear (×) button when a value is selected. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Shows the value but prevents editing. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading spinner inside the field. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Switches the field to the error color role. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error text shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /**
   * Validation rules applied to the selected value.
   */
  rules: {
    type: Array,
    default: null,
  },
  /**
   * When true, validation runs only after the first blur (not on every change).
   */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /**
   * Suppresses the reserved space below the field for hint/error messages.
   */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /**
   * Reduces the field height to the M3 dense density level.
   */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the selection changes; payload is the new value (or array when `multiple` is true). */
  'update:modelValue': null,
});

// Spread onto the q-select root via effectiveProps, which is why the component sets
// `inheritAttrs: false` — the default fallthrough would apply attrs (and listeners) twice.
const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-select',
  `hr-select--${props.variant}`,
  props.dense && 'hr-select--dense',
]);

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    options: props.options,
    ...(props.label !== null && { label: props.label }),
    ...(props.hint !== null && { hint: props.hint }),
    ...(props.errorMessage !== null && { errorMessage: props.errorMessage }),
    optionLabel: props.optionLabel,
    optionValue: props.optionValue,
    emitValue: props.emitValue,
    mapOptions: props.mapOptions,
    multiple: props.multiple,
    useInput: props.useInput,
    hideSelected: props.hideSelected,
    fillInput: props.fillInput,
    inputDebounce: props.inputDebounce,
    ...(props.maxValues !== null && { maxValues: props.maxValues }),
    ...(props.newValueMode !== null && { newValueMode: props.newValueMode }),
    ...(props.displayValue !== null && { displayValue: props.displayValue }),
    clearable: props.clearable,
    disable: props.disable,
    readonly: props.readonly,
    loading: props.loading,
    error: props.error,
    dense: props.dense,
    ...(props.rules !== null && { rules: props.rules }),
    lazyRules: props.lazyRules,
    hideBottomSpace: props.hideBottomSpace,
  };

  // Derive the container style from the M3 `variant` in BOTH themes (mirrors
  // HrTextField) so legacy consumers get the outlined look from `variant` rather
  // than having to pass `legacyProps: { outlined: true }`.
  const variantStyle = {
    filled: props.variant === 'filled',
    outlined: props.variant === 'outlined',
  };

  // An explicit Quasar container style passed by the caller wins outright, in both themes,
  // so QSelect never receives two competing styles (e.g. outlined + standout) at once.
  const containerStyleKeys = ['filled', 'outlined', 'standout', 'borderless'];
  const hasContainerStyle = (source) => containerStyleKeys.some((key) => key in source);

  if (hasTheme.value) {
    return {
      ...base,
      ...(hasContainerStyle(attrs) ? {} : variantStyle),
      noErrorIcon: true,
      popupContentClass: 'hr-select__popup',
      ...attrs,
    };
  }

  // Legacy theme: legacyProps is honored alongside attrs.
  const overrides = { ...props.legacyProps, ...attrs };
  return { ...base, ...(hasContainerStyle(overrides) ? {} : variantStyle), ...overrides };
});

const selectRef = ref(null);
defineExpose({
  hidePopup: () => selectRef.value?.hidePopup(),
  add: (...args) => selectRef.value?.add(...args),
  /** Runs the field's validation rules; returns a boolean or a Promise resolving to one. */
  validate: (...args) => selectRef.value?.validate(...args),
});
</script>

<template>
  <q-select
    ref="selectRef"
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template
      v-if="$slots.prepend"
      #prepend="slotProps"
    >
      <!-- @slot prepend Content placed before the field container (e.g. a leading icon). -->
      <slot
        name="prepend"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.append"
      #append="slotProps"
    >
      <!-- @slot append Content placed after the field container (e.g. a trailing icon). -->
      <slot
        name="append"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots['selected-item']"
      #selected-item="slotProps"
    >
      <!-- @slot selected-item Custom rendering of each selected chip/item; receives Quasar scope props. -->
      <slot
        name="selected-item"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.option"
      #option="slotProps"
    >
      <!-- @slot option Custom rendering of each dropdown option; receives Quasar scope props including `itemProps`. -->
      <slot
        name="option"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots['no-option']"
      #no-option="slotProps"
    >
      <!-- @slot no-option Content shown when the options list is empty (e.g. after filtering). -->
      <slot
        name="no-option"
        v-bind="slotProps ?? {}"
      />
    </template>
    <template
      v-if="$slots.hint"
      #hint="slotProps"
    >
      <!-- @slot hint Custom hint content rendered below the field. -->
      <slot
        name="hint"
        v-bind="slotProps ?? {}"
      />
    </template>
  </q-select>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Common ─────────────────────────────────────────────────────────────── */

.hr-select :deep(.q-field__label) {
  color: var(--app-on-surface-variant);
  transition: color 150ms linear;
}

.hr-select :deep(.q-field__native),
.hr-select :deep(.q-field__input) {
  color: var(--app-on-surface);
  caret-color: var(--app-primary);
}

.hr-select :deep(.q-field__messages),
.hr-select :deep(.q-field__counter) {
  color: var(--app-on-surface-variant);
}

/* Focused: label shifts to primary */
.hr-select.q-field--focused :deep(.q-field__label) {
  color: var(--app-primary);
}

/* Error: label and supporting text use error color */
.hr-select.q-field--error :deep(.q-field__label) {
  color: var(--app-error);
}

.hr-select.q-field--error :deep(.q-field__messages) {
  color: var(--app-error);
}

.hr-select.q-field--error :deep(.q-field__native),
.hr-select.q-field--error :deep(.q-field__input) {
  caret-color: var(--app-error);
}

/* Leading / trailing icons */
.hr-select :deep(.q-field__prepend),
.hr-select :deep(.q-field__append) {
  color: var(--app-on-surface-variant);
}

.hr-select :deep(.q-field__prefix),
.hr-select :deep(.q-field__suffix) {
  color: var(--app-on-surface-variant);
}

/* Disabled */
.hr-select.q-field--disabled :deep(.q-field__label),
.hr-select.q-field--disabled :deep(.q-field__native),
.hr-select.q-field--disabled :deep(.q-field__messages),
.hr-select.q-field--disabled :deep(.q-field__counter),
.hr-select.q-field--disabled :deep(.q-field__prepend),
.hr-select.q-field--disabled :deep(.q-field__append),
.hr-select.q-field--disabled :deep(.q-field__prefix),
.hr-select.q-field--disabled :deep(.q-field__suffix) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* ── Filled ─────────────────────────────────────────────────────────────── */

.hr-select--filled :deep(.q-field__control) {
  background: var(--app-surface-container-highest);
  border-radius: var(--app-shape-corner-extra-small) var(--app-shape-corner-extra-small) 0 0;
}

/*
 * ::before is Quasar's hover-state overlay + resting active-indicator line.
 * Reset Quasar's default grey background; keep only the M3 bottom border.
 */
.hr-select--filled :deep(.q-field__control::before) {
  background: transparent;
  border-bottom: 1px solid var(--app-on-surface-variant);
  border-radius: 0;
}

/* Hover state layer over the filled container */
.hr-select--filled :deep(.q-field__control:hover::before) {
  background: color-mix(in srgb, var(--app-on-surface) 8%, transparent);
}

/*
 * When focused, Quasar clears ::before so ::after (the animated indicator)
 * takes over. Explicitly confirm that here so our border-bottom doesn't bleed through.
 */
.hr-select--filled.q-field--focused :deep(.q-field__control::before),
.hr-select--filled.q-field--highlighted :deep(.q-field__control::before) {
  background: transparent;
  border-bottom-color: transparent;
}

/* ::after is Quasar's animated active-indicator line (scales in on focus) */
.hr-select--filled :deep(.q-field__control::after) {
  background: var(--app-primary);
}

/* Error: bottom indicator uses error color both at rest and when focused */
.hr-select--filled.q-field--error :deep(.q-field__control::before) {
  border-bottom-color: var(--app-error);
}

.hr-select--filled.q-field--error :deep(.q-field__control::after) {
  background: var(--app-error);
}

/* Disabled: dimmed container + dimmed indicator */
.hr-select--filled.q-field--disabled :deep(.q-field__control) {
  background: color-mix(in srgb, var(--app-on-surface) 4%, transparent);
}

.hr-select--filled.q-field--disabled :deep(.q-field__control::before) {
  border-bottom-color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* ── Outlined ───────────────────────────────────────────────────────────── */

.hr-select--outlined :deep(.q-field__control) {
  border-radius: var(--app-shape-corner-extra-small);
}

/* ::before is Quasar's border box for outlined */
.hr-select--outlined :deep(.q-field__control::before) {
  border: 1px solid var(--app-outline);
  border-radius: inherit;
}

/* Hover: border darkens to on-surface */
.hr-select--outlined :deep(.q-field__control:hover::before) {
  border-color: var(--app-on-surface);
}

/* Focused: 2 dp primary border */
.hr-select--outlined.q-field--focused :deep(.q-field__control::before) {
  border: 2px solid var(--app-primary);
}

/* Error: border uses error color */
.hr-select--outlined.q-field--error :deep(.q-field__control::before) {
  border-color: var(--app-error);
}

.hr-select--outlined.q-field--error.q-field--focused :deep(.q-field__control::before) {
  border: 2px solid var(--app-error);
}

/* Disabled: border fades to 12% on-surface */
.hr-select--outlined.q-field--disabled :deep(.q-field__control::before) {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

</style>

<style lang="scss">

/*
 * Not scoped — Quasar teleports the select dropdown outside the component DOM,
 * so scoped styles cannot reach it. The [data-theme] guard ensures these rules
 * only apply when a design system theme is active.
 */

/* ── Dropdown popup ─────────────────────────────────────────────────────── */
/*
 * Quasar applies popupContentClass directly to the q-menu root element, so
 * .hr-select__popup lands on the same element as .q-menu. [data-theme] ensures
 * this only fires when a design system theme is active; the html element always
 * remains an ancestor of the teleported menu even when it's attached to body.
 */
[data-theme] .hr-select__popup {
  background: var(--app-surface-container);
  box-shadow: var(--app-elevation-2);
}

/* ── Mobile dialog header ───────────────────────────────────────────────── */
/*
 * On mobile, Quasar renders the select as a QDialog. The dialog wraps a QField
 * (the header/search bar) + the options list div (.hr-select__popup). The QField
 * is teleported outside the component DOM so scoped styles cannot reach it; the
 * options list is already themed via popupContentClass. These rules apply
 * identical filled-field theming to the dialog header.
 */

[data-theme] .q-select__dialog {
  background: var(--app-surface-container);
}

[data-theme] .q-select__dialog .q-field__label {
  color: var(--app-on-surface-variant);
  transition: color 150ms linear;
}

[data-theme] .q-select__dialog .q-field--focused .q-field__label {
  color: var(--app-primary);
}

[data-theme] .q-select__dialog .q-field__native,
[data-theme] .q-select__dialog .q-field__input {
  color: var(--app-on-surface);
  caret-color: var(--app-primary);
}

[data-theme] .q-select__dialog .q-field__prepend,
[data-theme] .q-select__dialog .q-field__append {
  color: var(--app-on-surface-variant);
}

[data-theme] .q-select__dialog .q-field__control {
  background: var(--app-surface-container-highest);
}

[data-theme] .q-select__dialog .q-field__control::before {
  background: transparent;
  border-bottom: 1px solid var(--app-on-surface-variant);
  border-radius: 0;
}

[data-theme] .q-select__dialog .q-field__control::after {
  background: var(--app-primary);
}

[data-theme] .q-select__dialog .q-field--focused .q-field__control::before {
  background: transparent;
  border-bottom-color: transparent;
}

/* ── Option items ───────────────────────────────────────────────────────── */

[data-theme] .hr-select__popup .q-item {
  color: var(--app-on-surface);
}

[data-theme] .hr-select__popup .q-item--active {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

[data-theme] .hr-select__popup .q-item .q-focus-helper {
  background: var(--app-on-surface);
}

[data-theme] .hr-select__popup .q-item--active .q-focus-helper {
  background: var(--app-on-secondary-container);
}

[data-theme] .hr-select__popup .q-item.hr-text-primary {
  --hr-on-container: var(--app-primary);
  color: var(--app-primary);
}

[data-theme] .hr-select__popup .q-item.hr-text-primary .q-focus-helper {
  background: var(--app-primary);
}

</style>
